import React from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, FileText, Download } from "lucide-react";
import { TERMS_OF_USE, PRIVACY_POLICY } from "../legal/content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Legal document viewer — renders either the Terms of Use or the Privacy
 * Policy as a clean, readable page with a Back button and a "Download PDF"
 * link to the canonical signed PDF stored on the backend.
 */
export default function LegalDocPage({ kind }) {
  const navigate = useNavigate();
  const isTerms = kind === "terms";
  const title = isTerms ? "Terms of Use" : "Privacy Policy";
  const body = isTerms ? TERMS_OF_USE : PRIVACY_POLICY;
  const pdfUrl = `${BACKEND_URL}/api/static/legal/${isTerms ? "terms" : "privacy"}.pdf`;

  return (
    <div
      className="min-h-screen bg-[#0A0A0A] text-white"
      data-testid={`legal-page-${kind}`}
    >
      <div className="border-b border-white/10 bg-black/60 sticky top-0 backdrop-blur z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-white/60 hover:text-white text-xs uppercase tracking-[0.2em]"
            data-testid={`legal-back-${kind}`}
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#FFCC00]" />
            <span className="font-heading text-lg uppercase">{title}</span>
          </div>
          <div className="flex-1" />
          <a
            href={pdfUrl}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1.5 text-white/60 hover:text-[#FFCC00] text-xs uppercase tracking-[0.2em]"
            data-testid={`legal-download-${kind}`}
          >
            <Download className="w-3.5 h-3.5" /> PDF
          </a>
        </div>
      </div>

      <article className="max-w-3xl mx-auto px-6 py-12 prose-doc">
        <Markdown text={body} />
      </article>

      <div className="border-t border-white/10 mt-8 py-6 text-center text-[10px] tracking-[0.3em] uppercase text-white/30">
        © Colin Darke Solutions LLC · Nitro Auto Heist
      </div>
    </div>
  );
}

/**
 * Tiny markdown renderer for h2 headings, paragraphs, and bullets.
 * Avoids pulling in a dependency for a static doc.
 */
function Markdown({ text }) {
  const blocks = text.split(/\n\n+/);
  return (
    <>
      {blocks.flatMap((block, i) => {
        // If a block starts with a heading marker, peel ONLY the heading
        // line off and render the rest as a separate paragraph below.
        if (block.startsWith("## ") || block.startsWith("### ")) {
          const newlineIdx = block.indexOf("\n");
          const headingLine = newlineIdx === -1 ? block : block.slice(0, newlineIdx);
          const rest = newlineIdx === -1 ? "" : block.slice(newlineIdx + 1).trim();
          const isH3 = block.startsWith("### ");
          const HeadingTag = isH3 ? "h3" : "h2";
          const headingClass = isH3
            ? "font-heading text-lg text-white mt-6 mb-2"
            : "font-heading text-2xl text-[#FFCC00] mt-10 mb-3";
          const headingText = headingLine.replace(/^#{2,3}\s+/, "");
          const out = [
            <HeadingTag key={`h-${i}`} className={headingClass}>
              {headingText}
            </HeadingTag>,
          ];
          if (rest) {
            out.push(<Paragraph key={`p-${i}`} text={rest} />);
          }
          return out;
        }
        // Bullet list
        if (block.split("\n").every((l) => l.startsWith("- "))) {
          return [
            <ul key={`ul-${i}`} className="list-disc pl-6 my-3 space-y-1.5 text-white/75 text-sm leading-relaxed">
              {block.split("\n").map((line, j) => (
                <li key={j}>{line.replace(/^-\s+/, "")}</li>
              ))}
            </ul>,
          ];
        }
        return [<Paragraph key={`p-${i}`} text={block} />];
      })}
    </>
  );
}

function Paragraph({ text }) {
  const lines = text.split("\n");
  return (
    <p className="my-3 text-white/75 text-sm leading-relaxed">
      {lines.map((line, j) => (
        <React.Fragment key={j}>
          {line}
          {j < lines.length - 1 && <br />}
        </React.Fragment>
      ))}
    </p>
  );
}
